import { LoanOfficer, KPI } from '../data/mockData'
import { DateRange } from '../components/DashboardHeader'

export function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`
  }
  return `$${value.toFixed(0)}`
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

export function formatMinutes(value: number): string {
  const hours = Math.floor(value / 60)
  const mins = Math.floor(value % 60)
  if (hours === 0) return `${mins}m`
  return `${hours}h ${mins}m`
}

export type PerformanceColor = 'green' | 'yellow' | 'red'

export function getPerformanceColor(
  metric: string,
  value: number,
): PerformanceColor {
  switch (metric) {
    case 'totalCalls':
      return value >= 50 ? 'green' : value >= 35 ? 'yellow' : 'red'
    case 'totalEngagedCalls':
      return value >= 40 ? 'green' : value >= 25 ? 'yellow' : 'red'
    case 'conversations60Plus':
      return value >= 25 ? 'green' : value >= 15 ? 'yellow' : 'red'
    case 'totalTalkTime':
      return value >= 180 ? 'green' : value >= 120 ? 'yellow' : 'red'
    case 'avgTalkTimePerCall':
      return value >= 3.5 ? 'green' : value >= 2.5 ? 'yellow' : 'red'
    case 'avgAiCallScore':
      return value >= 75 ? 'green' : value >= 55 ? 'yellow' : 'red'
    case 'totalTransfers':
      return value >= 12 ? 'green' : value >= 6 ? 'yellow' : 'red'
    case 'transferRate':
      return value >= 20 ? 'green' : value >= 15 ? 'yellow' : 'red'
    case 'qualifiedTransfers':
      return value >= 10 ? 'green' : value >= 5 ? 'yellow' : 'red'
    case 'qualifiedTransferPct':
      return value >= 80 ? 'green' : value >= 60 ? 'yellow' : 'red'
    case 'failedTransfers':
      return value <= 1 ? 'green' : value <= 3 ? 'yellow' : 'red'
    case 'failedTransferPct':
      return value <= 10 ? 'green' : value <= 20 ? 'yellow' : 'red'
    case 'transferToSubmissionPct':
      return value >= 65 ? 'green' : value >= 45 ? 'yellow' : 'red'
    case 'submissionToSignaturePct':
      return value >= 80 ? 'green' : value >= 60 ? 'yellow' : 'red'
    case 'activePipelineCount':
      return value >= 20 ? 'green' : value >= 10 ? 'yellow' : 'red'
    case 'activePipelineValue':
      return value >= 5000000 ? 'green' : value >= 2500000 ? 'yellow' : 'red'
    case 'documentsCollected':
      return value >= 15 ? 'green' : value >= 8 ? 'yellow' : 'red'
    case 'docsCollectedPct':
      return value >= 85 ? 'green' : value >= 65 ? 'yellow' : 'red'
    default:
      return 'green'
  }
}

export function getTrendColor(trend: 'up' | 'down', metric: string): string {
  const invertedMetrics = [
    'failedTransfers',
    'failedTransferPct',
    'activeCoachingFlags',
  ]
  const isInverted = invertedMetrics.includes(metric)

  if (trend === 'up') {
    return isInverted ? 'text-red-500' : 'text-green-500'
  } else {
    return isInverted ? 'text-green-500' : 'text-red-500'
  }
}

export function exportToCSV(data: any[], filename: string) {
  if (data.length === 0) return

  const headers = Object.keys(data[0]).filter(
    (key) => key !== 'sparklineData' && key !== 'coachingFlags',
  )

  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header]
          const stringValue = String(value).replace(/"/g, '""')
          return stringValue.includes(',') ? `"${stringValue}"` : stringValue
        })
        .join(','),
    ),
  ]

  const csvContent = csvRows.join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function generateSparkline(value: number, points: number = 7): number[] {
  const result: number[] = []
  for (let i = 0; i < points; i++) {
    const progress = (i + 1) / points
    const noise = 1 + Math.sin(i * 2.5) * 0.08
    result.push(Math.round(value * progress * noise * 10) / 10)
  }
  return result
}

export function computeKPIsFromLOs(loanOfficers: LoanOfficer[]): KPI[] {
  if (loanOfficers.length === 0) {
    return [
      {
        id: 'totalCalls',
        label: 'Total Calls',
        value: 0,
        formattedValue: '0',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'conversations60Plus',
        label: 'Conversations 60+s',
        value: 0,
        formattedValue: '0',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'totalTransfers',
        label: 'Total Transfers',
        value: 0,
        formattedValue: '0',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'qualifiedTransfers',
        label: 'Qualified Transfers',
        value: 0,
        formattedValue: '0',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'transferRate',
        label: 'Transfer Rate',
        value: 0,
        formattedValue: '0.0%',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'failedTransferPct',
        label: 'Failed Transfer %',
        value: 0,
        formattedValue: '0.0%',
        change: 0,
        trend: 'down' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'activePipelineCount',
        label: 'Active Pipeline',
        value: 0,
        formattedValue: '0',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        id: 'activePipelineValue',
        label: 'Pipeline $',
        value: 0,
        formattedValue: '$0',
        change: 0,
        trend: 'up' as const,
        sparklineData: [0, 0, 0, 0, 0, 0, 0],
      },
    ]
  }

  const sum = (fn: (lo: LoanOfficer) => number) =>
    loanOfficers.reduce((acc, lo) => acc + fn(lo), 0)
  const avg = (fn: (lo: LoanOfficer) => number) => sum(fn) / loanOfficers.length

  const totalCalls = sum((lo) => lo.totalCalls)
  const totalConversations = sum((lo) => lo.conversations60Plus)
  const totalTransfers = sum((lo) => lo.totalTransfers)
  const qualifiedTransfers = sum((lo) => lo.qualifiedTransfers)
  const avgTransferRate = Math.round(avg((lo) => lo.transferRate) * 10) / 10
  const avgFailedTransferPct =
    Math.round(avg((lo) => lo.failedTransferPct) * 10) / 10
  const activePipelineCount = sum((lo) => lo.activePipelineCount)
  const activePipelineValue = sum((lo) => lo.activePipelineValue)

  return [
    {
      id: 'totalCalls',
      label: 'Total Calls',
      value: totalCalls,
      formattedValue: totalCalls.toLocaleString(),
      change: 8.2,
      trend: 'up',
      sparklineData: generateSparkline(totalCalls),
    },
    {
      id: 'conversations60Plus',
      label: 'Conversations 60+s',
      value: totalConversations,
      formattedValue: totalConversations.toLocaleString(),
      change: 5.4,
      trend: 'up',
      sparklineData: generateSparkline(totalConversations),
    },
    {
      id: 'totalTransfers',
      label: 'Total Transfers',
      value: totalTransfers,
      formattedValue: totalTransfers.toLocaleString(),
      change: 12.1,
      trend: 'up',
      sparklineData: generateSparkline(totalTransfers),
    },
    {
      id: 'qualifiedTransfers',
      label: 'Qualified Transfers',
      value: qualifiedTransfers,
      formattedValue: qualifiedTransfers.toLocaleString(),
      change: 9.5,
      trend: 'up',
      sparklineData: generateSparkline(qualifiedTransfers),
    },
    {
      id: 'transferRate',
      label: 'Transfer Rate',
      value: avgTransferRate,
      formattedValue: `${avgTransferRate.toFixed(1)}%`,
      change: 2.1,
      trend: 'up',
      sparklineData: generateSparkline(avgTransferRate),
    },
    {
      id: 'failedTransferPct',
      label: 'Failed Transfer %',
      value: avgFailedTransferPct,
      formattedValue: `${avgFailedTransferPct.toFixed(1)}%`,
      change: -1.2,
      trend: 'down',
      sparklineData: generateSparkline(avgFailedTransferPct),
    },
    {
      id: 'activePipelineCount',
      label: 'Active Pipeline',
      value: activePipelineCount,
      formattedValue: activePipelineCount.toLocaleString(),
      change: 6.8,
      trend: 'up',
      sparklineData: generateSparkline(activePipelineCount),
    },
    {
      id: 'activePipelineValue',
      label: 'Pipeline $',
      value: activePipelineValue,
      formattedValue: formatCurrency(activePipelineValue),
      change: 14.5,
      trend: 'up',
      sparklineData: generateSparkline(activePipelineValue / 1000000),
    },
  ]
}

function seedOffset(id: string, metric: string): number {
  let hash = 0
  const str = id + metric
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0
  }
  return ((Math.abs(hash) % 20) - 10) / 100
}

function scaleCount(
  base: number,
  factor: number,
  id: string,
  metric: string,
): number {
  const offset = seedOffset(id, metric)
  return Math.max(0, Math.round(base * factor * (1 + offset)))
}

function scalePct(
  base: number,
  id: string,
  metric: string,
  variance: number,
): number {
  const offset = seedOffset(id, metric)
  const result = base + offset * variance * 100
  return Math.max(0, Math.min(100, Math.round(result * 10) / 10))
}

function scaleCurrency(
  base: number,
  factor: number,
  id: string,
  metric: string,
): number {
  const offset = seedOffset(id, metric)
  return Math.max(0, Math.round(base * factor * (1 + offset)))
}

function getCountFactor(dateRange: DateRange): number {
  switch (dateRange) {
    case 'Today':
      return 1 / 30
    case 'WTD':
      return 1 / 6
    case '7D':
      return 7 / 30
    case 'MTD':
      return 20 / 30
    case '30D':
      return 1
    case 'Custom':
      return 0.5
  }
}

function getPctVariance(dateRange: DateRange): number {
  switch (dateRange) {
    case 'Today':
      return 1.5
    case 'WTD':
      return 1.2
    case '7D':
      return 0.8
    case 'MTD':
      return 0.4
    case '30D':
      return 0
    case 'Custom':
      return 0.6
  }
}

export function getScaledLoanOfficer(
  lo: LoanOfficer,
  dateRange: DateRange,
): LoanOfficer {
  if (dateRange === '30D') return lo

  const cf = getCountFactor(dateRange)
  const pv = getPctVariance(dateRange)
  const id = lo.id

  const scaled: LoanOfficer = {
    ...lo,
    totalCalls: scaleCount(lo.totalCalls, cf, id, 'tc'),
    totalEngagedCalls: scaleCount(lo.totalEngagedCalls, cf, id, 'tec'),
    conversations60Plus: scaleCount(lo.conversations60Plus, cf, id, 'c60'),
    totalTalkTime: scaleCount(lo.totalTalkTime, cf, id, 'ttt'),
    avgTalkTimePerCall: scalePct(lo.avgTalkTimePerCall, id, 'att', pv / 10), // slight variance
    avgAiCallScore: scalePct(lo.avgAiCallScore, id, 'aics', pv / 8),
    totalTransfers: scaleCount(lo.totalTransfers, cf, id, 'tt'),
    transferRate: scalePct(lo.transferRate, id, 'tr', pv),
    qualifiedTransfers: scaleCount(lo.qualifiedTransfers, cf, id, 'qt'),
    qualifiedTransferPct: scalePct(lo.qualifiedTransferPct, id, 'qtp', pv),
    failedTransfers: scaleCount(lo.failedTransfers, cf, id, 'ft'),
    failedTransferPct: scalePct(lo.failedTransferPct, id, 'ftp', pv),
    transferToSubmissionPct: scalePct(
      lo.transferToSubmissionPct,
      id,
      't2s',
      pv,
    ),
    submissionToSignaturePct: scalePct(
      lo.submissionToSignaturePct,
      id,
      's2s',
      pv,
    ),
    activePipelineCount: scaleCount(lo.activePipelineCount, cf, id, 'apc'),
    activePipelineValue: scaleCurrency(
      lo.activePipelineValue,
      cf > 0.5 ? cf : cf * 2,
      id,
      'apv',
    ),
    documentsCollected: scaleCount(lo.documentsCollected, cf, id, 'dc'),
    docsCollectedPct: scalePct(lo.docsCollectedPct, id, 'dcp', pv),
  }

  return scaled
}
